import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import AuthService from '~/modules/services/auth-service';
import UserService from '~/modules/services/user-service';

const TERMINAL_NAME = 'pixwel';

function createLine(type, text) {
	return {
		id: `${type}-${Date.now()}-${Math.random().toString(36)
			.slice(2, 8)}`,
		type,
		text
	};
}

// GitHub Dark Dimmed theme (https://github.com/primer/github-vscode-theme)
const GH = {
	bg: '#22272e',
	header: '#2d333b',
	border: '#444c56',
	text: '#adbac7',
	muted: '#768390',
	green: '#8ddb8c',
	red: '#f47067',
	orange: '#f69d50',
	purple: '#dcbdfb',
	blue: '#6cb6ff',
	cyan: '#96d0ff',
	prompt: '#6cb6ff'
};

const RAINBOW = [GH.red, GH.orange, GH.cyan, GH.green, GH.blue, GH.purple];

// prettier-ignore
const BANNER_ROWS = [
	' ___ _____  ____      _____ _    ',
	'| _ \\_ _\\ \\/ /\\ \\    / / __| |   ',
	'|  _/| | >  <  \\ \\/\\/ /| _|| |__',
	'|_| |___/_/\\_\\  \\_/\\_/ |___|____|'
];

function buildBannerLines() {
	return BANNER_ROWS.map((row, i) => ({
		id: `banner-${i}`,
		type: 'banner',
		text: row,
		color: RAINBOW[i % RAINBOW.length]
	}));
}

function buildWelcomeLines(isImpersonating) {
	return [
		...buildBannerLines(),
		createLine('muted', ''),
		createLine('system', 'Pixwel admin console online. Type `help` to list commands.'),
		isImpersonating
			? createLine('warning', 'You are currently impersonating another user. Use `switchback` to return to your admin session.')
			: createLine('muted', 'Available now: `whoami`, `impersonate <email>`, `sudo <email>`, `switchback`, `clear`.')
	];
}

function buildPrompt(session) {
	const email = session?.emailNormal || session?.username || '';
	const identity = email.includes('@') ? email.split('@')[0] : (email || 'admin');
	return `${TERMINAL_NAME}:${identity}$`;
}

function buildHelpLines(isImpersonating) {
	return [
		createLine('system', 'Commands:'),
		createLine('muted', 'help                     Show available commands'),
		createLine('muted', 'whoami / me / session    Pretty-print current session'),
		createLine('muted', 'impersonate <email>      Become a user by email address'),
		createLine('muted', 'sudo <email>             Alias for impersonate'),
		createLine('muted', '<email>                  Shorthand for impersonate'),
		createLine('muted', 'clear                    Clear console output'),
		createLine('muted', 'switchback               Return to the original admin session'),
		createLine('muted', 'exit                     Switch back if impersonating, else close'),
		createLine('muted', `Impersonating: ${isImpersonating ? 'yes' : 'no'}`)
	];
}

function tokenizeJsonLine(row) {
	// "key": <value>
	const keyValue = row.match(/^(\s*)("(?:[^"\\]|\\.)*")(\s*:\s*)(.+)$/);
	if(keyValue) {
		const [, indent, key, colon, rest] = keyValue;
		const segments = [
			{ text: indent, color: GH.muted },
			{ text: key, color: GH.purple },
			{ text: colon, color: GH.muted }
		];

		const strVal = rest.match(/^("(?:[^"\\]|\\.)*")(,?)$/);
		const numVal = rest.match(/^(-?\d[\d.eE+]*)(,?)$/);
		const boolNull = rest.match(/^(true|false|null)(,?)$/);

		if(strVal) {
			segments.push({ text: strVal[1], color: GH.cyan });
			if(strVal[2]) {
				segments.push({ text: strVal[2], color: GH.muted });
			}
		}
		else if(numVal) {
			segments.push({ text: numVal[1], color: GH.orange });
			if(numVal[2]) {
				segments.push({ text: numVal[2], color: GH.muted });
			}
		}
		else if(boolNull) {
			segments.push({ text: boolNull[1], color: GH.orange });
			if(boolNull[2]) {
				segments.push({ text: boolNull[2], color: GH.muted });
			}
		}
		else {
			segments.push({ text: rest, color: GH.muted });
		}

		return segments;
	}

	// bare string (array element)
	const bareStr = row.match(/^(\s*)("(?:[^"\\]|\\.)*")(,?)$/);
	if(bareStr) {
		return [
			{ text: bareStr[1], color: GH.muted },
			{ text: bareStr[2], color: GH.cyan },
			{ text: bareStr[3], color: GH.muted }
		];
	}

	// brackets, braces, punctuation
	return [{ text: row, color: GH.muted }];
}

function buildWhoamiLines(session, isImpersonating) {
	const lines = [createLine('system', 'Session:')];

	const record = { ...session, impersonating: isImpersonating };
	const json = JSON.stringify(record, null, 2);
	json.split('\n').forEach((row) => lines.push({
		id: `whoami-${Date.now()}-${Math.random().toString(36)
			.slice(2, 8)}`,
		type: 'json',
		text: row,
		segments: tokenizeJsonLine(row)
	}));

	return lines;
}

const COMMANDS = ['help', 'whoami', 'me', 'session', 'impersonate', 'sudo', 'switchback', 'clear', 'exit'];

function tabComplete(rawInput) {
	const value = rawInput.trimStart();

	if(value.includes(' ')) {
		return null;
	}

	const lower = value.toLowerCase();
	const matches = COMMANDS.filter((cmd) => cmd.startsWith(lower));

	if(matches.length === 0 || lower === '') {
		return null;
	}

	if(matches.length === 1) {
		const needsArg = matches[0] === 'impersonate' || matches[0] === 'sudo';
		return { completed: matches[0] + (needsArg ? ' ' : ''), matches: null };
	}

	return { completed: null, matches };
}

function parseCommand(rawInput) {
	const tokens = rawInput.trim().split(/\s+/)
		.filter(Boolean);
	const [name = '', ...args] = tokens;

	return {
		name: name.toLowerCase(),
		args
	};
}

const DEFAULT_SIZE = { width: 720, height: 520 };
const DEFAULT_POS = null; // null = centred on first open

function useFloating(open) {
	const [pos, setPos] = React.useState(DEFAULT_POS);
	const [size, setSize] = React.useState(DEFAULT_SIZE);
	const dragRef = React.useRef(null);
	const resizeRef = React.useRef(null);
	const panelRef = React.useRef(null);

	// Centre on first open
	React.useEffect(() => {
		if(open && !pos) {
			setPos({
				x: Math.max(0, (window.innerWidth - DEFAULT_SIZE.width) / 2),
				y: Math.max(0, (window.innerHeight - DEFAULT_SIZE.height) / 2)
			});
		}
	}, [open, pos]);

	const onDragMouseDown = React.useCallback((e) => {
		if(e.button !== 0) {
			return;
		}

		e.preventDefault();
		dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos?.x ?? 0, origY: pos?.y ?? 0 };

		const onMove = (ev) => {
			const dx = ev.clientX - dragRef.current.startX;
			const dy = ev.clientY - dragRef.current.startY;
			setPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
		};

		const onUp = () => {
			dragRef.current = null;
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
		};

		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
	}, [pos]);

	const onResizeMouseDown = React.useCallback((e) => {
		if(e.button !== 0) {
			return;
		}

		e.preventDefault();
		resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: size.width, origH: size.height };

		const onMove = (ev) => {
			const dw = ev.clientX - resizeRef.current.startX;
			const dh = ev.clientY - resizeRef.current.startY;
			setSize({
				width: Math.max(480, resizeRef.current.origW + dw),
				height: Math.max(320, resizeRef.current.origH + dh)
			});
		};

		const onUp = () => {
			resizeRef.current = null;
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
		};

		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
	}, [size]);

	return { pos, size, panelRef, onDragMouseDown, onResizeMouseDown };
}

function AdminConsole({ open, onOpenChange, session }) {
	const queryClient = useQueryClient();
	const inputRef = React.useRef(null);
	const outputRef = React.useRef(null);
	const [input, setInput] = React.useState('');
	const [isRunning, setIsRunning] = React.useState(false);
	const isImpersonating = AuthService.isImpersonating();
	const [lines, setLines] = React.useState(() => buildWelcomeLines(isImpersonating));
	const { pos, size, panelRef, onDragMouseDown, onResizeMouseDown } = useFloating(open);

	const prompt = React.useMemo(() => buildPrompt(session), [session]);

	const appendLines = React.useCallback((nextLines) => {
		setLines((currentLines) => [...currentLines, ...nextLines]);
	}, []);

	// Scroll output to bottom when lines change
	React.useEffect(() => {
		if(outputRef.current) {
			outputRef.current.scrollTop = outputRef.current.scrollHeight;
		}
	}, [lines]);

	React.useEffect(() => {
		if(!open) {
			setInput('');
			setIsRunning(false);
			return;
		}

		setLines(buildWelcomeLines(AuthService.isImpersonating()));
		window.setTimeout(() => inputRef.current?.focus(), 0);
	}, [open]);

	// Close on Escape
	React.useEffect(() => {
		if(!open) {
			return;
		}

		const onKeyDown = (e) => {
			if(e.key === 'Escape') {
				onOpenChange(false);
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [open, onOpenChange]);

	const runImpersonation = React.useCallback(async(email) => {
		if(!email) {
			appendLines([createLine('error', 'Usage: impersonate <email>')]);
			return;
		}

		setIsRunning(true);

		try {
			const user = await UserService.getUserByEmail(email);

			if(!user?.id && !user?._id) {
				appendLines([createLine('error', `No user found for \`${email}\`.`)]);
				return;
			}

			const userId = user.id || user._id;
			const userEmail = user.emailNormal || email;

			if(userId === (session?.id || session?._id)) {
				appendLines([createLine('warning', `Already signed in as \`${userEmail}\`.`)]);
				return;
			}

			appendLines([
				createLine('success', `Resolved user \`${user.name || userEmail}\` <${userEmail}>.`),
				createLine('success', 'Switching session now...')
			]);
			queryClient.clear();
			await AuthService.become(userId);
		}
		catch{
			appendLines([createLine('error', `Failed to impersonate \`${email}\`.`)]);
		}
		finally {
			setIsRunning(false);
		}
	}, [appendLines, queryClient, session]);

	const runSwitchBack = React.useCallback(async() => {
		if(!AuthService.isImpersonating()) {
			appendLines([createLine('error', 'No impersonated session is active.')]);
			return;
		}

		setIsRunning(true);
		appendLines([createLine('success', 'Restoring original admin session...')]);

		try {
			queryClient.clear();
			await AuthService.switchBack();
		}
		finally {
			setIsRunning(false);
		}
	}, [appendLines, queryClient]);

	const handleKeyDown = React.useCallback((event) => {
		if(event.key !== 'Tab') {
			return;
		}

		event.preventDefault();

		const result = tabComplete(input);
		if(!result) {
			return;
		}

		if(result.completed) {
			setInput(result.completed);
		}
		else if(result.matches) {
			appendLines([createLine('muted', result.matches.join('    '))]);
		}
	}, [input, appendLines]);

	const handleSubmit = async(event) => {
		event.preventDefault();

		const rawInput = input.trim();
		if(!rawInput || isRunning) {
			return;
		}

		appendLines([createLine('command', `${prompt} ${rawInput}`)]);
		setInput('');

		const { name, args } = parseCommand(rawInput);

		switch(name) {
			case 'help':
				appendLines(buildHelpLines(AuthService.isImpersonating()));
				return;
			case 'clear':
				setLines([]);
				return;
			case 'whoami':
			case 'me':
			case 'session':
				appendLines(buildWhoamiLines(session, AuthService.isImpersonating()));
				return;
			case 'impersonate':
			case 'sudo':
				await runImpersonation(args.join(' '));
				return;
			case 'switchback':
				await runSwitchBack();
				return;
			case 'exit':
				if(AuthService.isImpersonating()) {
					await runSwitchBack();
				}
				else {
					onOpenChange(false);
				}
				return;
			default:
				if(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(name)) {
					await runImpersonation(name);
					return;
				}
				appendLines([createLine('error', `Command not found: \`${name || rawInput}\``)]);
		}
	};

	if(!open || !pos) {
		return null;
	}

	const lineColor = (line) => {
		if(line.color) {
			return line.color;
		}

		switch(line.type) {
			case 'error': return GH.red;
			case 'success': return GH.green;
			case 'warning': return GH.orange;
			case 'muted': return GH.muted;
			case 'command': return GH.cyan;
			default: return GH.text;
		}
	};

	return ReactDOM.createPortal(
		<div
			ref={panelRef}
			data-testid="admin-console-panel"
			style={{
				position: 'fixed',
				left: pos.x,
				top: pos.y,
				width: size.width,
				height: size.height,
				zIndex: 9999,
				display: 'flex',
				flexDirection: 'column',
				backgroundColor: GH.bg,
				color: GH.text,
				border: `1px solid ${GH.border}`,
				borderRadius: '10px',
				overflow: 'hidden',
				boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
				fontFamily: 'var(--font-mono, monospace)',
				fontSize: '13px'
			}}
		>
			{/* Title bar — drag handle */}
			<div
				onMouseDown={onDragMouseDown}
				style={{
					backgroundColor: GH.header,
					borderBottom: `1px solid ${GH.border}`,
					padding: '12px 16px 10px',
					cursor: 'grab',
					userSelect: 'none',
					flexShrink: 0
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
					{/* macOS traffic lights */}
					<button
						type="button"
						aria-label="Close"
						onClick={() => onOpenChange(false)}
						style={{
							width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff5f57', border: 'none', cursor: 'pointer', padding: 0
						}}
					/>
					<span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#febc2e', display: 'inline-block' }} />
					<span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#28c840', display: 'inline-block' }} />
				</div>
				<div style={{ color: GH.text, fontWeight: 600 }}>Pixwel Admin Console</div>
				<div style={{ color: GH.muted, fontSize: 11, marginTop: 2 }}>
					Terminal-style admin tools for impersonation and session diagnostics.
				</div>
			</div>

			{/* Output */}
			<div
				ref={outputRef}
				data-testid="admin-console-output"
				style={{
					flex: 1,
					overflowY: 'auto',
					padding: '16px 20px',
					scrollbarWidth: 'none',
					msOverflowStyle: 'none'
				}}
			>
				<style>{`[data-testid="admin-console-output"]::-webkit-scrollbar { display: none; }`}</style>
				{lines.map((line) => (
					<div
						key={line.id}
						style={{
							whiteSpace: 'pre',
							wordBreak: 'break-all',
							lineHeight: '1.6',
							color: lineColor(line),
							...(line.type === 'banner' && { lineHeight: '1.2', letterSpacing: 0 })
						}}
					>
						{line.segments
							? line.segments.map((seg) => (
								<span key={seg.text + seg.color} style={{ color: seg.color }}>{seg.text}</span>
							))
							: line.text
						}
					</div>
				))}
			</div>

			{/* Input */}
			<form
				onSubmit={handleSubmit}
				style={{
					borderTop: `1px solid ${GH.border}`,
					padding: '10px 20px',
					display: 'flex',
					alignItems: 'center',
					gap: 12,
					flexShrink: 0
				}}
			>
				<span style={{ color: GH.prompt, whiteSpace: 'nowrap' }}>{prompt}</span>
				<input
					ref={inputRef}
					data-testid="admin-console-input"
					style={{
						flex: 1,
						background: 'transparent',
						border: 'none',
						outline: 'none',
						color: GH.text,
						fontFamily: 'inherit',
						fontSize: 'inherit',
						padding: 0,
						height: 28
					}}
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={handleKeyDown}
					disabled={isRunning}
					autoCapitalize="none"
					autoComplete="off"
					autoCorrect="off"
					spellCheck={false}
				/>
			</form>

			{/* Resize handle */}
			<div
				onMouseDown={onResizeMouseDown}
				style={{
					position: 'absolute',
					bottom: 0,
					right: 0,
					width: 16,
					height: 16,
					cursor: 'nwse-resize',
					opacity: 0.4
				}}
			>
				<svg width="16" height="16" viewBox="0 0 16 16" fill={GH.muted}>
					<path d="M11 5h2v2h-2zM8 8h2v2H8zM5 11h2v2H5zM11 8h2v2h-2zM8 11h2v2H8zM11 11h2v2h-2z" />
				</svg>
			</div>
		</div>,
		document.body
	);
}

AdminConsole.propTypes = {
	open: PropTypes.bool.isRequired,
	onOpenChange: PropTypes.func.isRequired,
	session: PropTypes.object.isRequired
};

export default AdminConsole;
